import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trash2,
  Archive,
  Pin,
  Tag,
  Pencil,
  X,
  AlertTriangle,
  Music,
  Play,
  Pause,
  Clock,
  Hash,
  FileText,
  MessageSquare,
  Globe,
  Link,
  Copy,
  Check,
  CheckCircle2,
  Search,
  Download,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Loader2,
  Wand2,
} from "lucide-react";
import type { SoundResponse, TagResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { api } from "@/lib/api";
import { cn, formatDuration, formatPlayCount } from "@/lib/utils";

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  variant,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  variant: "danger" | "warning";
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-[18px] border border-mist bg-slate p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full", variant === "danger" ? "bg-ember/15" : "bg-spark/15")}>
            <AlertTriangle className={cn("h-4 w-4", variant === "danger" ? "text-ember" : "text-spark")} />
          </div>
          <div>
            <h3 className="font-display text-base font-semibold text-parchment">{title}</h3>
            <p className="mt-1 text-sm text-fog">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-xl px-4 py-2 text-sm text-fog hover:bg-slate-raised"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={cn(
              "rounded-xl px-4 py-2 text-sm font-medium text-ink",
              variant === "danger" ? "bg-ember hover:bg-ember/80" : "bg-spark hover:bg-spark-soft",
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

type BulkStep = "transcribing" | "suggesting" | "reviewing" | "saving";

function BulkAiSuggestModal({
  sounds,
  onClose,
  onComplete,
}: {
  sounds: SoundResponse[];
  onClose: () => void;
  onComplete: () => void;
}) {
  const { play, stop, isPlaying } = useAudio();
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState<BulkStep>("transcribing");
  const [editTranscript, setEditTranscript] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagDraft, setEditTagDraft] = useState("");
  const editTagRef = useRef<HTMLInputElement>(null);
  const [accepted, setAccepted] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [failed, setFailed] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const processingRef = useRef(false);

  const current = sounds[index] as SoundResponse | undefined;

  // Process current sound: transcribe → suggest → review
  // Deps intentionally exclude `step` — the async function manages step
  // transitions internally. Including `step` causes the cleanup to set
  // cancelledRef=true on every setStep() call, silently aborting processing.
  useEffect(() => {
    if (!current || done) return;
    if (processingRef.current) return;
    processingRef.current = true;
    cancelledRef.current = false;
    setError(null);

    (async () => {
      try {
        // Step 1: Always transcribe fresh from audio
        setStep("transcribing");
        let transcript = "";
        try {
          const res = await api.admin.transcribeById(current.id);
          if (cancelledRef.current) return;
          transcript = res.text;
        } catch {
          // Fall back to existing transcript if transcription fails
          transcript = current.transcript ?? "";
        }

        // Step 2: Get AI suggestions
        setStep("suggesting");
        const res = await api.admin.aiSuggest({
          title: current.title,
          transcript,
        });
        if (cancelledRef.current) return;

        const existingTags = current.tags.map((t) => t.name);
        const mergedTags = [...new Set([...existingTags, ...(res.tags ?? [])])];

        setEditTranscript(transcript);
        setEditDescription(res.description || current.description || "");
        setEditTags(mergedTags);
        setStep("reviewing");
      } catch {
        if (!cancelledRef.current) {
          setError("AI processing failed for this sound");
          setStep("reviewing");
          // Pre-fill with existing data so user can still skip
          setEditTranscript(current.transcript ?? "");
          setEditDescription(current.description ?? "");
          setEditTags(current.tags.map((t) => t.name));
        }
      } finally {
        processingRef.current = false;
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, done]);

  const moveNext = useCallback(() => {
    processingRef.current = false;
    if (index + 1 >= sounds.length) {
      setDone(true);
    } else {
      setIndex((i) => i + 1);
      setStep("transcribing");
      setError(null);
      setEditTagDraft("");
    }
  }, [index, sounds.length]);

  const handleAccept = async () => {
    if (!current) return;
    setStep("saving");
    try {
      // Commit any draft tag
      const finalTags = [...editTags];
      const leftover = editTagDraft.trim().toLowerCase();
      if (leftover && !finalTags.includes(leftover)) {
        finalTags.push(leftover);
      }

      await api.admin.updateSound(current.id, {
        transcript: editTranscript || null,
        description: editDescription || null,
        tags: finalTags,
      });
      setAccepted((a) => a + 1);
    } catch {
      setFailed((f) => f + 1);
    }
    moveNext();
  };

  const handleSkip = () => {
    setSkipped((s) => s + 1);
    moveNext();
  };

  const handleStop = () => {
    cancelledRef.current = true;
    setDone(true);
  };

  const pct = sounds.length > 0 ? Math.round(((done ? sounds.length : index) / sounds.length) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-2 backdrop-blur-sm sm:py-6 sm:px-4">
      <div className="flex w-full max-w-lg flex-col rounded-[18px] border border-mist bg-slate shadow-xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-3rem)]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-mist px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-spark/15">
              <Wand2 className="h-4 w-4 text-spark" />
            </div>
            <div>
              <h2 className="font-display text-base font-bold tracking-display text-parchment">
                Bulk AI Suggest
              </h2>
              <p className="font-mono text-[10px] text-fog">
                {done ? "Complete" : `${index + 1} of ${sounds.length}`}
                {" · "}
                <span className="text-moss">{accepted} accepted</span>
                {" · "}
                <span>{skipped} skipped</span>
                {failed > 0 && <>{" · "}<span className="text-ember">{failed} failed</span></>}
              </p>
            </div>
          </div>
          {done && (
            <button onClick={onClose} className="rounded-lg p-1.5 text-fog hover:bg-slate-raised">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <div className="h-1.5 overflow-hidden rounded-full bg-mist">
            <div
              className={cn("h-full rounded-full transition-all duration-300", done ? "bg-moss" : "bg-spark")}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {done ? (
            /* Done state */
            <div className="py-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-moss" />
              <p className="mt-3 font-display text-lg font-semibold text-parchment">All done!</p>
              <p className="mt-1 text-sm text-fog">
                {accepted} sound{accepted !== 1 ? "s" : ""} updated
                {skipped > 0 && `, ${skipped} skipped`}
                {failed > 0 && `, ${failed} failed`}
              </p>
              <button
                onClick={() => { onComplete(); onClose(); }}
                className="mt-5 rounded-xl bg-spark px-6 py-2 text-sm font-medium text-ink hover:bg-spark-soft"
              >
                Done
              </button>
            </div>
          ) : (step === "transcribing" || step === "suggesting") && current ? (
            /* Loading state */
            <div className="py-10 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-spark" />
              <p className="mt-3 text-sm font-medium text-parchment">{current.title}</p>
              <p className="mt-1 font-mono text-[11px] text-fog">
                {step === "transcribing" ? "Transcribing audio…" : "Generating suggestions…"}
              </p>
              <button
                onClick={handleStop}
                className="mt-6 rounded-xl border border-mist px-4 py-1.5 text-sm text-fog hover:bg-slate-raised"
              >
                Stop
              </button>
            </div>
          ) : step === "reviewing" && current ? (
            /* Review state */
            <div className="space-y-3">
              {/* Sound header */}
              <div className="flex items-center gap-3 rounded-xl bg-ink/50 p-3">
                <button
                  onClick={() => {
                    if (isPlaying(current.id)) stop(current.id);
                    else play(current.id, current.audio_url, { title: current.title, durationMs: current.duration_ms, slug: current.slug });
                  }}
                  className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors", isPlaying(current.id) ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft")}
                >
                  {isPlaying(current.id) ? <Pause className="h-3 w-3 text-ink" /> : <Play className="ml-0.5 h-3 w-3 text-ink" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-parchment">{current.title}</p>
                  <p className="font-mono text-[10px] text-fog">{formatDuration(current.duration_ms)}</p>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-xl bg-ember/10 px-3 py-2 text-xs text-ember">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {error}
                </div>
              )}

              {/* Transcript */}
              <div>
                <label className="mb-1 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Transcript</label>
                <textarea
                  value={editTranscript}
                  onChange={(e) => setEditTranscript(e.target.value)}
                  rows={2}
                  placeholder="No transcript"
                  className="w-full resize-none rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  placeholder="AI-generated description"
                  className="w-full resize-none rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="mb-1 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Tags</label>
                <div
                  className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-xl border border-mist bg-ink px-2 py-1.5 focus-within:border-spark focus-within:ring-2 focus-within:ring-spark/20"
                  onClick={() => editTagRef.current?.focus()}
                >
                  {editTags.map((tag) => (
                    <span key={tag} className="flex items-center gap-1 rounded-lg bg-spark/15 px-2 py-0.5 text-xs text-spark">
                      {tag}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditTags((prev) => prev.filter((t) => t !== tag)); }}
                        className="ml-0.5 rounded-full p-0.5 hover:bg-spark/20"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={editTagRef}
                    type="text"
                    value={editTagDraft}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.includes(",")) {
                        for (const part of val.split(",")) {
                          const trimmed = part.trim().toLowerCase();
                          if (trimmed && !editTags.includes(trimmed)) {
                            setEditTags((prev) => [...prev, trimmed]);
                          }
                        }
                        setEditTagDraft("");
                        return;
                      }
                      setEditTagDraft(val);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Tab" || e.key === "Enter") {
                        const trimmed = editTagDraft.trim().toLowerCase();
                        if (trimmed && !editTags.includes(trimmed)) {
                          e.preventDefault();
                          setEditTags((prev) => [...prev, trimmed]);
                          setEditTagDraft("");
                        } else if (trimmed) {
                          e.preventDefault();
                          setEditTagDraft("");
                        }
                      } else if (e.key === "Backspace" && !editTagDraft && editTags.length > 0) {
                        setEditTags((prev) => prev.slice(0, -1));
                      }
                    }}
                    placeholder={editTags.length === 0 ? "Add tags…" : ""}
                    className="min-w-[60px] flex-1 bg-transparent px-1 py-0.5 text-sm text-parchment placeholder:text-fog/50 outline-none"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={handleStop}
                  className="text-sm text-fog hover:text-parchment"
                >
                  Stop
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleSkip}
                    className="rounded-xl border border-mist px-4 py-1.5 text-sm text-fog hover:bg-slate-raised"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleAccept}
                    className="rounded-xl bg-spark px-4 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft"
                  >
                    Accept{index + 1 < sounds.length ? " & Next" : ""}
                  </button>
                </div>
              </div>
            </div>
          ) : step === "saving" && current ? (
            /* Saving state */
            <div className="py-10 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-spark" />
              <p className="mt-3 text-sm text-fog">Saving changes…</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const MIME_EXT: Record<string, string> = {
  "audio/mpeg": ".mp3",
  "audio/mp3": ".mp3",
  "audio/wav": ".wav",
  "audio/x-wav": ".wav",
  "audio/wave": ".wav",
  "audio/ogg": ".ogg",
  "audio/flac": ".flac",
  "audio/mp4": ".m4a",
  "audio/x-m4a": ".m4a",
  "audio/aac": ".aac",
  "audio/webm": ".webm",
};

function EditSoundModal({
  sound,
  onClose,
  onSaved,
}: {
  sound: SoundResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { play, stop, isPlaying } = useAudio();
  const playing = isPlaying(sound.id);
  const [title, setTitle] = useState(sound.title);
  const [description, setDescription] = useState(sound.description ?? "");
  const [transcript, setTranscript] = useState(sound.transcript ?? "");
  const [tagInput, setTagInput] = useState(sound.tags.map((t) => t.name).join(", "));
  const [isPinned, setIsPinned] = useState(Boolean(sound.is_pinned));
  const [isPublished, setIsPublished] = useState(Boolean(sound.published_at));
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const suggestingRef = useRef(false);

  const handleAiSuggest = async () => {
    if (!title && !transcript) return;
    if (suggestingRef.current) return;
    suggestingRef.current = true;
    setSuggesting(true);
    setSuggestError(null);
    try {
      const res = await api.admin.aiSuggest({ title, transcript });
      if (res.description) setDescription(res.description);
      if (res.tags?.length) {
        const existing = tagInput.split(",").map((t) => t.trim()).filter(Boolean);
        const merged = [...new Set([...existing, ...res.tags])];
        setTagInput(merged.join(", "));
      }
    } catch (err) {
      setSuggestError(err instanceof Error ? err.message : "AI suggest failed — try again");
    } finally {
      setSuggesting(false);
      suggestingRef.current = false;
    }
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      const res = await api.admin.transcribeById(sound.id);
      setTranscript(res.text);
    } catch {}
    setTranscribing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const tags = tagInput.split(",").map((t) => t.trim()).filter(Boolean);
      await api.admin.updateSound(sound.id, {
        title,
        description: description || null,
        transcript: transcript || null,
        tags,
        published_at: isPublished ? (sound.published_at || Date.now()) : null,
      });
      if (isPinned !== Boolean(sound.is_pinned)) {
        await api.admin.pinSound(sound.id, isPinned);
      }
      onSaved();
    } catch {
      // handled silently
    }
    setSaving(false);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const audioUrl = `${window.location.origin}/clip/${sound.slug}.${sound.mime_type?.includes("wav") ? "wav" : "mp3"}`;
  const permalink = `${window.location.origin}/s/${sound.slug}`;
  const triggerUrl = `${window.location.origin}/trigger/${sound.slug}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-2 backdrop-blur-sm sm:py-8 sm:px-4">
      <div className="flex w-full max-w-lg flex-col rounded-[18px] border border-mist bg-slate shadow-xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-4rem)]">
        <div className="flex shrink-0 items-center justify-between border-b border-mist px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-spark/15">
              <Music className="h-4 w-4 text-spark" />
            </div>
            <h2 className="font-display text-lg font-bold tracking-display text-parchment">Edit Sound</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-fog hover:bg-slate-raised">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-3 rounded-xl bg-ink/50 p-3">
            <button
              onClick={() => playing ? stop(sound.id) : play(sound.id, sound.audio_url, { title: sound.title, durationMs: sound.duration_ms, slug: sound.slug })}
              className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors", playing ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft")}
            >
              {playing ? <Pause className="h-4 w-4 text-ink" /> : <Play className="ml-0.5 h-4 w-4 text-ink" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-parchment">{sound.title}</p>
              <div className="flex items-center gap-3 font-mono text-[10px] text-fog">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDuration(sound.duration_ms)}</span>
                <span>{formatPlayCount(sound.play_count)} plays</span>
                <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{sound.slug}</span>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(sound.audio_url);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${sound.title}${MIME_EXT[sound.mime_type] || ""}`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch {
                  window.open(sound.audio_url, "_blank");
                }
              }}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-raised transition-colors hover:bg-mist"
              title="Download file"
            >
              <Download className="h-4 w-4 text-fog" />
            </button>
          </div>

          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={handleAiSuggest}
                disabled={suggesting || (!title && !transcript)}
                className="flex items-center gap-1.5 rounded-xl bg-spark/10 px-3 py-1.5 text-sm font-medium text-spark transition-colors hover:bg-spark/20 disabled:opacity-50"
              >
                {suggesting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wand2 className="h-3.5 w-3.5" />
                )}
                {suggesting ? "Generating…" : "AI suggest description & tags"}
              </button>
              {suggestError && (
                <span className="text-xs text-red-400">{suggestError}</span>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <span className="flex items-center gap-1"><FileText className="h-3 w-3" />Description</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional short description"
              className="w-full resize-none rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <label className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
                <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />Transcript</span>
              </label>
              <button
                onClick={handleTranscribe}
                disabled={transcribing}
                className="flex items-center gap-1 rounded-lg bg-spark/10 px-2 py-0.5 font-mono text-[10px] font-medium text-spark transition-colors hover:bg-spark/20 disabled:opacity-50"
              >
                {transcribing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {transcribing ? "Transcribing…" : "Auto-transcribe"}
              </button>
            </div>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={2}
              placeholder="What is said in this sound clip"
              className="w-full resize-none rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <span className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags</span>
            </label>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Comma separated (e.g. Funny, Angry, Joe)"
              className="w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
            {tagInput && (
              <div className="mt-2 flex flex-wrap gap-1">
                {tagInput.split(",").map((t) => t.trim()).filter(Boolean).map((tag) => (
                  <span key={tag} className="rounded-full bg-spark/10 px-2 py-0.5 font-mono text-[10px] text-spark">{tag}</span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                className="accent-spark"
              />
              <span className="flex items-center gap-1 text-sm text-parchment">
                <Pin className="h-3.5 w-3.5 text-spark" /> Pinned
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="accent-spark"
              />
              <span className="flex items-center gap-1 text-sm text-parchment">
                <Globe className="h-3.5 w-3.5 text-moss" /> Published
              </span>
            </label>
          </div>

          <div className="rounded-xl border border-mist bg-ink/50 p-3">
            <p className="mb-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Share URLs</p>
            <div className="space-y-1.5">
              {[
                { label: "Permalink", url: permalink, icon: Link },
                { label: "Audio", url: audioUrl, icon: Music },
                { label: "Trigger", url: triggerUrl, icon: Play },
              ].map(({ label, url, icon: Icon }) => (
                <button
                  key={label}
                  onClick={() => handleCopy(url, label)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-mist/50"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 text-fog" />
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fog">{url}</span>
                  {copied === label ? (
                    <Check className="h-3.5 w-3.5 shrink-0 text-moss" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 shrink-0 text-fog" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-mist px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm text-fog hover:bg-slate-raised"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="rounded-xl bg-spark px-5 py-2 text-sm font-medium text-ink hover:bg-spark-soft disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

type SortColumn = "title" | "duration" | "plays" | "created";
type SortDir = "asc" | "desc";

export function AdminSounds() {
  const [sounds, setSounds] = useState<SoundResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingSound, setEditingSound] = useState<SoundResponse | null>(null);
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [selectedTag, setSelectedTag] = useState("");
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [sortCol, setSortCol] = useState<SortColumn>("created");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [confirmAction, setConfirmAction] = useState<{
    type: "archive" | "delete";
    soundId: string;
    soundTitle: string;
  } | null>(null);
  const [bulkAiSounds, setBulkAiSounds] = useState<SoundResponse[] | null>(null);

  const toggleSort = useCallback((col: SortColumn) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return col;
      }
      setSortDir(col === "title" ? "asc" : "desc");
      return col;
    });
  }, []);

  const sortedSounds = useMemo(() => {
    const sorted = [...sounds];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case "title": cmp = a.title.localeCompare(b.title); break;
        case "duration": cmp = a.duration_ms - b.duration_ms; break;
        case "plays": cmp = a.play_count - b.play_count; break;
        case "created": cmp = a.created_at - b.created_at; break;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
    return sorted;
  }, [sounds, sortCol, sortDir]);

  useEffect(() => {
    if (searchQuery === "") {
      setDebouncedSearch("");
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const fetchSounds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.listSounds({
        q: debouncedSearch || undefined,
        tags: selectedTag || undefined,
        limit: 500,
        sort: "newest",
      });
      setSounds(res.sounds);
      setTotalCount(res.total);
    } catch {}
    setLoading(false);
  }, [debouncedSearch, selectedTag]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.tags.list();
      setTags(res);
    } catch {}
  }, []);

  useEffect(() => {
    fetchSounds();
  }, [fetchSounds]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === sounds.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sounds.map((s) => s.id)));
    }
  };

  const handlePin = async (id: string, currentlyPinned: boolean) => {
    await api.admin.pinSound(id, !currentlyPinned);
    fetchSounds();
  };

  const handleConfirmedAction = async () => {
    if (!confirmAction) return;
    if (confirmAction.type === "archive") {
      await api.admin.archiveSound(confirmAction.soundId);
    } else {
      await api.admin.deleteSound(confirmAction.soundId);
    }
    setConfirmAction(null);
    fetchSounds();
  };

  const handleBulkTag = async (action: "add" | "remove") => {
    const tags = bulkTagInput.split(",").map((t) => t.trim()).filter(Boolean);
    if (tags.length === 0 || selected.size === 0) return;

    await api.admin.bulkTags({
      sound_ids: [...selected],
      ...(action === "add" ? { add_tags: tags } : { remove_tags: tags }),
    });

    setBulkTagInput("");
    setShowBulkTag(false);
    setSelected(new Set());
    fetchSounds();
  };

  if (loading && sounds.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  return (
    <div className={cn(loading && "pointer-events-none")}>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-display">
          Sounds <span className="text-fog">({totalCount})</span>
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const targets = selected.size > 0
                ? sounds.filter((s) => selected.has(s.id))
                : sounds;
              setBulkAiSounds(targets);
            }}
            className="flex items-center gap-1.5 rounded-xl bg-slate-raised px-3 py-1.5 text-sm text-parchment hover:bg-mist"
          >
            <Wand2 className="h-3.5 w-3.5" />
            Bulk AI suggest{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
          {selected.size > 0 && (
            <button
              onClick={() => setShowBulkTag(!showBulkTag)}
              className="flex items-center gap-1.5 rounded-xl bg-slate-raised px-3 py-1.5 text-sm text-parchment hover:bg-mist"
            >
              <Tag className="h-4 w-4" />
              Bulk tag ({selected.size})
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, description, transcript..."
            className="h-9 w-full rounded-xl border border-mist bg-ink pl-9 pr-8 text-sm text-parchment outline-none placeholder:text-fog/50 focus:border-spark focus:ring-2 focus:ring-spark/20"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-fog hover:text-parchment"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          className="h-9 appearance-none rounded-xl border border-mist bg-ink px-3 pr-8 text-sm text-parchment outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
        >
          <option value="">All tags</option>
          {tags.map((tag) => (
            <option key={tag.id} value={tag.slug}>
              {tag.name} ({tag.sound_count})
            </option>
          ))}
        </select>
      </div>

      {showBulkTag && (
        <div className="mt-4 flex items-center gap-2 rounded-[18px] border border-mist bg-slate p-3">
          <input
            type="text"
            value={bulkTagInput}
            onChange={(e) => setBulkTagInput(e.target.value)}
            placeholder="Tag names (comma separated)"
            className="flex-1 rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment placeholder:text-fog outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
          />
          <button
            onClick={() => handleBulkTag("add")}
            className="rounded-xl bg-spark px-3 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft"
          >
            Add tags
          </button>
          <button
            onClick={() => handleBulkTag("remove")}
            className="rounded-xl bg-ember px-3 py-1.5 text-sm font-medium text-ink hover:bg-ember/80"
          >
            Remove tags
          </button>
          <button
            onClick={() => setShowBulkTag(false)}
            className="rounded-xl p-1.5 text-fog hover:bg-slate-raised"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-[18px] border border-mist">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-mist bg-slate">
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={selected.size === sounds.length && sounds.length > 0}
                  onChange={selectAll}
                  className="accent-spark"
                />
              </th>
              <th className="px-3 py-2 text-left">
                <button onClick={() => toggleSort("title")} className={cn("flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "title" ? "text-spark" : "text-fog hover:text-parchment")}>
                  Title
                  {sortCol === "title" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-fog">
                Tags
              </th>
              <th className="px-3 py-2 text-right">
                <button onClick={() => toggleSort("duration")} className={cn("ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "duration" ? "text-spark" : "text-fog hover:text-parchment")}>
                  Duration
                  {sortCol === "duration" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </button>
              </th>
              <th className="px-3 py-2 text-right">
                <button onClick={() => toggleSort("plays")} className={cn("ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "plays" ? "text-spark" : "text-fog hover:text-parchment")}>
                  Plays
                  {sortCol === "plays" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </button>
              </th>
              <th className="px-3 py-2 text-right">
                <button onClick={() => toggleSort("created")} className={cn("ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "created" ? "text-spark" : "text-fog hover:text-parchment")}>
                  Created
                  {sortCol === "created" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </button>
              </th>
              <th className="w-32 px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-fog">
                Actions
              </th>
            </tr>
          </thead>
          <AnimatePresence mode="popLayout">
            {sortedSounds.map((sound) => (
              <motion.tr
                key={sound.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  "border-b border-mist last:border-0",
                  selected.has(sound.id) && "bg-spark/5",
                )}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(sound.id)}
                    onChange={() => toggleSelect(sound.id)}
                    className="accent-spark"
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {sound.is_pinned ? (
                      <Pin className="h-3 w-3 shrink-0 text-spark" />
                    ) : null}
                    <button
                      onClick={() => setEditingSound(sound)}
                      className="truncate text-left text-parchment hover:text-spark"
                    >
                      {sound.title}
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {sound.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full bg-slate-raised px-1.5 py-0.5 font-mono text-[10px] text-fog"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-fog">
                  {formatDuration(sound.duration_ms)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-fog">
                  {formatPlayCount(sound.play_count)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[11px] text-fog">
                  {new Date(sound.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-0.5">
                    <button
                      onClick={() => setEditingSound(sound)}
                      className="group/btn relative rounded-lg p-1.5 text-fog transition-colors hover:bg-spark/10 hover:text-spark"
                      title="Edit sound"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity group-hover/btn:opacity-100">Edit</span>
                    </button>
                    <button
                      onClick={() =>
                        handlePin(sound.id, Boolean(sound.is_pinned))
                      }
                      className={cn(
                        "group/btn relative rounded-lg p-1.5 transition-colors",
                        sound.is_pinned ? "text-spark hover:bg-spark/10" : "text-fog hover:bg-spark/10 hover:text-spark",
                      )}
                      title="Toggle pin"
                    >
                      <Pin className="h-3.5 w-3.5" />
                      <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity group-hover/btn:opacity-100">{sound.is_pinned ? "Unpin" : "Pin"}</span>
                    </button>
                    <button
                      onClick={() => setConfirmAction({ type: "archive", soundId: sound.id, soundTitle: sound.title })}
                      className="group/btn relative rounded-lg p-1.5 text-fog transition-colors hover:bg-spark/10 hover:text-spark"
                      title="Archive sound"
                    >
                      <Archive className="h-3.5 w-3.5" />
                      <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity group-hover/btn:opacity-100">Archive</span>
                    </button>
                    <button
                      onClick={() => setConfirmAction({ type: "delete", soundId: sound.id, soundTitle: sound.title })}
                      className="group/btn relative rounded-lg p-1.5 text-fog transition-colors hover:bg-ember/10 hover:text-ember"
                      title="Delete sound"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-ink px-2 py-1 font-mono text-[10px] text-parchment opacity-0 shadow-lg transition-opacity group-hover/btn:opacity-100">Delete</span>
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </AnimatePresence>
        </table>
      </div>

      {editingSound && (
        <EditSoundModal
          sound={editingSound}
          onClose={() => setEditingSound(null)}
          onSaved={() => {
            setEditingSound(null);
            fetchSounds();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction?.type === "delete" ? "Delete sound" : "Archive sound"}
        message={
          confirmAction?.type === "delete"
            ? `Permanently delete "${confirmAction.soundTitle}"? This cannot be undone.`
            : `Archive "${confirmAction?.soundTitle ?? ""}"? It will be hidden from the public soundboard.`
        }
        confirmLabel={confirmAction?.type === "delete" ? "Delete" : "Archive"}
        variant={confirmAction?.type === "delete" ? "danger" : "warning"}
        onConfirm={handleConfirmedAction}
        onCancel={() => setConfirmAction(null)}
      />

      {bulkAiSounds && (
        <BulkAiSuggestModal
          sounds={bulkAiSounds}
          onClose={() => setBulkAiSounds(null)}
          onComplete={() => {
            setSelected(new Set());
            fetchSounds();
          }}
        />
      )}
    </div>
  );
}
